import { useState } from "react";
import { Link } from "react-router-dom";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, formatApiError } from "@/lib/api";
import { Mail, CheckCircle2 } from "lucide-react";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [emailSent, setEmailSent] = useState(true);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email });
      setEmailSent(data?.email_sent !== false);
      setSent(true);
    } catch (err) {
      setError(formatApiError(err));
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-brand-cream p-6" data-testid="forgot-password-page">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-lg border border-border p-10">
        <div className="mb-8"><Logo /></div>
        {!sent ? (
          <>
            <div className="w-12 h-12 rounded-xl bg-brand-orange/10 flex items-center justify-center mb-4">
              <Mail className="w-5 h-5 brand-orange" />
            </div>
            <h2 className="font-heading font-bold text-2xl">Forgot your password?</h2>
            <p className="text-foreground/60 text-sm mt-2">No worries. Enter your email and we&rsquo;ll send you a reset link.</p>
            <form onSubmit={submit} className="mt-6 space-y-4">
              <div>
                <Label htmlFor="email">Email</Label>
                <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1.5" data-testid="forgot-email-input" />
              </div>
              {error && <div className="text-sm text-destructive" data-testid="forgot-error">{error}</div>}
              <Button type="submit" disabled={loading} className="w-full bg-brand-orange hover:bg-[#E65C00] text-white h-11 rounded-full font-semibold" data-testid="forgot-submit-btn">
                {loading ? "Sending…" : "Send reset link"}
              </Button>
            </form>
          </>
        ) : (
          <div data-testid="forgot-sent-state">
            {emailSent ? (
              <>
                <div className="w-12 h-12 rounded-xl bg-veg/10 flex items-center justify-center mb-4">
                  <CheckCircle2 className="w-5 h-5 text-veg" />
                </div>
                <h2 className="font-heading font-bold text-2xl">Check your inbox</h2>
                <p className="text-foreground/60 text-sm mt-2">
                  If an account exists for <span className="font-semibold text-foreground">{email}</span>, a password reset link is on its way. The link expires in 1 hour.
                </p>
              </>
            ) : (
              <>
                <div className="w-12 h-12 rounded-xl bg-destructive/10 flex items-center justify-center mb-4">
                  <Mail className="w-5 h-5 text-destructive" />
                </div>
                <h2 className="font-heading font-bold text-2xl">Couldn&rsquo;t send email right now</h2>
                <p className="text-foreground/60 text-sm mt-2">
                  Our email provider rejected the request (usually a daily-quota issue or a typo in the email domain).
                  Please try again in a few minutes or contact <a className="brand-orange font-semibold hover:underline" href="mailto:support@menumojo.in">support@menumojo.in</a>.
                </p>
              </>
            )}
          </div>
        )}
        <div className="mt-6 text-sm text-foreground/60">
          <Link to="/login" className="brand-orange font-semibold hover:underline" data-testid="forgot-back-login">Back to sign in</Link>
        </div>
      </div>
    </div>
  );
}
